// Web auth flow for the standalone browser version (no Discord iframe, no Steam).
// Token lifecycle:
//   1. User hits /activity/, no token → show LoginScreen → button → window.location = '/auth/login'
//   2. Backend redirects to Discord, user authorizes, Discord redirects to /auth/callback?code=...
//   3. Backend exchanges code, redirects to /activity/#token=...&expires_in=...
//   4. consumeStoredToken() reads fragment, persists to localStorage, cleans URL
//   5. Subsequent loads read directly from localStorage until expiry

const TOKEN_KEY = 'fatewoven_token';
const EXPIRES_KEY = 'fatewoven_token_expires';

export type Platform = 'discord-iframe' | 'steam' | 'web';

interface SteamAuthGlobal {
  sessionToken?: string;
  platform?: string;
}

export function detectPlatform(): Platform {
  const steamAuth = (window as unknown as { __STEAM_AUTH__?: SteamAuthGlobal }).__STEAM_AUTH__;
  if (steamAuth?.sessionToken) return 'steam';
  if (window.location.search.includes('frame_id')) return 'discord-iframe';
  return 'web';
}

/**
 * Returns a valid token if one is available in the URL fragment (just-completed
 * OAuth callback) or in localStorage. Clears expired storage. URL fragment is
 * scrubbed after consumption so a refresh doesn't replay it.
 */
export function consumeStoredToken(): string | null {
  if (window.location.hash.startsWith('#token=') || window.location.hash.startsWith('#expires_in=') || window.location.hash.includes('token=')) {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const token = params.get('token');
    const expiresIn = parseInt(params.get('expires_in') || '0', 10);

    if (token) {
      const expiresAt = Date.now() + Math.max(0, expiresIn) * 1000;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(EXPIRES_KEY, String(expiresAt));
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return token;
    }
  }

  const stored = localStorage.getItem(TOKEN_KEY);
  const expiresAt = parseInt(localStorage.getItem(EXPIRES_KEY) || '0', 10);

  if (stored && Date.now() < expiresAt) {
    return stored;
  }

  if (stored) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  }
  return null;
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

export function redirectToLogin(): void {
  window.location.href = '/auth/login';
}
